/* eslint-disable no-empty-function */
import React, { createContext, useContext, useEffect, useState } from "react";

import { IModalContext, IProviderProps } from "../@types/ContextProviderRelated";
import { EModal, ESubMenu } from "../@types/Enums";
import { useDrawing } from "./DrawingProvider";
import { useExportContext } from "./ExportProvider";
import { useToolbarContext } from "./ToolbarProvider";

const modalDefaultValues: IModalContext = {
    isModalShown: false,
    setIsModalShown: () => {},
    activeModal: EModal.NONE,
    setActiveModal: () => {},
    selectedRoomName: "",
    setSelectedRoomName: () => {},
    selectedRoomId: 0,
    setSelectedRoomId: () => {}
};

export const ModalContext = createContext<IModalContext>(modalDefaultValues);

export const useModalContext = () => useContext(ModalContext);

export const ModalProvider = ({ children }: IProviderProps): JSX.Element => {
    const [isModalShown, setIsModalShown] = useState<boolean>(modalDefaultValues.isModalShown);
    const [activeModal, setActiveModal] = useState<EModal>(modalDefaultValues.activeModal);
    const [selectedRoomName, setSelectedRoomName] = useState<string>(modalDefaultValues.selectedRoomName);
    const [selectedRoomId, setSelectedRoomId] = useState<number>(modalDefaultValues.selectedRoomId);
    const { setCurrentlyActiveSubMenu, setIsDrawingAllowed } = useToolbarContext();
    const { roomCompleted } = useDrawing();
    const { setIsExporting } = useExportContext();

    // UseEffect hook to temporarily disable the drawing toast when a modal window is shown.
    useEffect(() => {
        if(isModalShown) {
            setIsDrawingAllowed(false);
        } else {
            if(!roomCompleted) {
                setIsDrawingAllowed(true);
            }
        }
    }, [isModalShown]);

    // Function to change the modal visibility (a.k.a. closing the currently active modal.)
    const setModalVisibility = (visible: boolean): void => {
        if(!visible) {
            // If at any point the modal gets closed, we reset the value for the active modal to the initial state, so that this modal opens when the user clicks in the menu.
            setActiveModal(EModal.CREATE_NEW);
            // IsExporting flag also needs a reset to its old value.
            setIsExporting(false);
        } else {
            // If we show a modal, we want all other possibly open menus to close themselves.
            setCurrentlyActiveSubMenu(ESubMenu.NONE);
        }
        setIsModalShown(visible);
    };

    return (
        <ModalContext.Provider value={{
            isModalShown,
            setIsModalShown: setModalVisibility,
            activeModal,
            setActiveModal,
            selectedRoomId,
            setSelectedRoomId,
            selectedRoomName,
            setSelectedRoomName
        }}>
            {children}
        </ModalContext.Provider>
    );
}